import React, { useEffect, useReducer, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";

/**
 * Pages where the verification gate should NOT be shown.
 *
 * The user must be able to log in, sign up, and complete verification
 * without the modal blocking them.
 */
const EXEMPT_PATHS = new Set(["/login", "/signup"]);

/**
 * Full-screen, non-dismissible overlay shown when a logged-in user hasn't
 * verified their email address.
 *
 * Anti-bypass measures:
 * - Rendered via a portal to document.body so it sits above everything.
 * - A MutationObserver watches for DOM removal and forces a re-render.
 * - Server-side middleware (requireEmailVerified) independently rejects
 *   unverified users on protected API routes — removing this overlay does
 *   not grant access.
 */
const EmailVerificationGuard: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading: authLoading, emailVerified, refreshEmailVerified } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [checking, setChecking] = useState(false);
  // The overlay-removal check below needs to force a re-render. It used to call
  // setEmailVerified(prev => prev), which React bails out of, so it never
  // actually re-rendered anything.
  const [, forceRender] = useReducer((n: number) => n + 1, 0);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [sendSuccess, setSendSuccess] = useState(false);

  const overlayRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const exempt = EXEMPT_PATHS.has(location.pathname);

  // ── Check verification status ──────────────────────────────────

  /**
   * The answer comes from AuthContext, which asks only once the session cookie
   * has been minted.
   *
   * This screen used to ask on its own, keyed on the Firebase user alone — so
   * the request went out the instant Firebase restored the user, before there
   * was a session to authenticate it. The server answered 401, the failure was
   * swallowed as "unknown" (which shows nothing), and nothing asked again until
   * Firebase happened to refresh its token. That is why the modal appeared
   * minutes later and seemingly at random rather than promptly or not at all.
   *
   * Asking in one place also means one request instead of two, and one answer
   * that cannot disagree with itself.
   */
  const checkStatus = useCallback(async () => {
    setChecking(true);
    try {
      await refreshEmailVerified();
    } finally {
      setChecking(false);
    }
  }, [refreshEmailVerified]);

  // ── Poll while unverified ──────────────────────────────────────

  useEffect(() => {
    if (emailVerified === false && !exempt) {
      // Poll every 15s so the modal auto-dismisses once the user verifies
      pollRef.current = setInterval(checkStatus, 15_000);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [emailVerified, exempt, checkStatus]);

  // ── Anti-tampering: MutationObserver ───────────────────────────

  useEffect(() => {
    if (emailVerified !== false || exempt) return;

    // Ensure the overlay is in the DOM. If something removes it, force a
    // re-render by toggling state.
    const enforce = () => {
      if (!overlayRef.current || !document.body.contains(overlayRef.current)) {
        // Force React to re-create the overlay
        forceRender();
      }
    };

    // Watch for the overlay being removed from body
    observerRef.current = new MutationObserver(() => {
      enforce();
    });

    observerRef.current.observe(document.body, { childList: true });

    // Also run periodically as a belt-and-suspenders check
    const interval = setInterval(enforce, 2_000);

    return () => {
      observerRef.current?.disconnect();
      clearInterval(interval);
    };
  }, [emailVerified, exempt]);

  // ── Resend verification email ──────────────────────────────────

  const handleResend = async () => {
    setSending(true);
    setSendError("");
    setSendSuccess(false);
    try {
      const email = user?.email || "";
      await axios.post(
        "/api/send-verification-email",
        { email },
        { withCredentials: true },
      );
      setSendSuccess(true);
    } catch (err: any) {
      setSendError(
        err?.response?.data?.error ||
          "Failed to send verification email. Please try again.",
      );
    } finally {
      setSending(false);
    }
  };

  const handleCheckAgain = () => {
    checkStatus();
  };

  // ── Render logic ───────────────────────────────────────────────

  // Still loading auth state — don't flash the overlay
  if (authLoading) {
    return <>{children}</>;
  }

  // No user, or user IS verified, or checking — show children
  if (!user || emailVerified !== false || exempt) {
    return <>{children}</>;
  }

  // User is logged in but email is unverified — show the gate
  return (
    <>
      {/* The app content is rendered behind the overlay but is
          inaccessible because the overlay captures all interaction.
          Server middleware independently blocks API calls. */}
      {children}

      {/* Full-screen overlay rendered at body level */}
      <div
        ref={overlayRef}
        id="email-verification-gate"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 2147483647, // max 32-bit signed int — above everything
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
        }}
      >
        <div
          style={{
            backgroundColor: "#ffffff",
            borderRadius: "16px",
            padding: "40px",
            maxWidth: "480px",
            width: "90%",
            boxShadow: "0 25px 60px rgba(0,0,0,0.3)",
            textAlign: "center",
            position: "relative",
          }}
        >
          {/* Icon */}
          <div style={{ marginBottom: "20px" }}>
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#1a56db"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ display: "inline-block" }}
            >
              <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>

          <h2
            style={{
              fontSize: "22px",
              fontWeight: 700,
              color: "#111827",
              marginBottom: "12px",
            }}
          >
            Verify Your Email Address
          </h2>

          <p
            style={{
              fontSize: "15px",
              color: "#6b7280",
              lineHeight: 1.6,
              marginBottom: "8px",
            }}
          >
            We sent a verification link to{" "}
            <strong style={{ color: "#111827" }}>
              {user?.email || "your email"}
            </strong>
            .
          </p>

          <p
            style={{
              fontSize: "14px",
              color: "#9ca3af",
              lineHeight: 1.5,
              marginBottom: "24px",
            }}
          >
            Please check your inbox and click the link to verify your account.
            Check your spam folder if you don't see it.
          </p>

          {/* Buttons */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <button
              type="button"
              onClick={handleCheckAgain}
              disabled={checking}
              style={{
                width: "100%",
                padding: "12px 20px",
                backgroundColor: checking ? "#93c5fd" : "#1a56db",
                color: "#ffffff",
                border: "none",
                borderRadius: "8px",
                fontSize: "15px",
                fontWeight: 600,
                cursor: checking ? "not-allowed" : "pointer",
                transition: "background-color 0.15s",
              }}
            >
              {checking ? "Checking..." : "I've Verified My Email"}
            </button>

            <button
              type="button"
              onClick={handleResend}
              disabled={sending}
              style={{
                width: "100%",
                padding: "12px 20px",
                backgroundColor: "transparent",
                color: sending ? "#9ca3af" : "#1a56db",
                border: "2px solid #1a56db",
                borderRadius: "8px",
                fontSize: "15px",
                fontWeight: 600,
                cursor: sending ? "not-allowed" : "pointer",
                transition: "all 0.15s",
              }}
            >
              {sending ? "Sending..." : "Resend Verification Email"}
            </button>
          </div>

          {/* Feedback messages */}
          {sendSuccess && (
            <p
              style={{
                marginTop: "14px",
                fontSize: "14px",
                color: "#059669",
                fontWeight: 500,
              }}
            >
              Verification email sent! Check your inbox.
            </p>
          )}
          {sendError && (
            <p
              style={{
                marginTop: "14px",
                fontSize: "14px",
                color: "#dc2626",
                fontWeight: 500,
              }}
            >
              {sendError}
            </p>
          )}

          {/* Sign out link */}
          <button
            type="button"
            onClick={() => navigate("/login")}
            style={{
              marginTop: "20px",
              background: "none",
              border: "none",
              color: "#6b7280",
              fontSize: "13px",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Back to Login
          </button>
        </div>
      </div>
    </>
  );
};

export default EmailVerificationGuard;
